import { Box, Dialog, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import PropTypes from "prop-types";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import ZoomOutRoundedIcon from "@mui/icons-material/ZoomOutRounded";
import CenterFocusStrongRoundedIcon from "@mui/icons-material/CenterFocusStrongRounded";
import useZoomPan from "hooks/useZoomPan";

const glassSx = {
  color: "#fff",
  bgcolor: "rgba(255,255,255,0.1)",
  border: "1px solid rgba(255,255,255,0.14)",
  backdropFilter: "blur(12px)",
  "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
  "&.Mui-disabled": { color: "rgba(255,255,255,0.3)" },
};

/** Full-screen image viewer: wheel, pinch, drag and double-click to zoom. */
function ImageLightbox({ src, alt, onClose }) {
  const { viewportRef, bind, style, scale, zoomBy, reset, minScale, maxScale } = useZoomPan({
    resetKey: src,
  });

  return (
    <Dialog
      open={Boolean(src)}
      onClose={onClose}
      fullScreen
      PaperProps={{ sx: { bgcolor: "rgba(2, 6, 23, 0.94)", backgroundImage: "none" } }}
      slotProps={{ backdrop: { sx: { backdropFilter: "blur(10px)" } } }}
    >
      <Box ref={viewportRef} {...bind} sx={{ position: "absolute", inset: 0, overflow: "hidden", ...bind.style }}>
        <Box
          component="img"
          src={src || undefined}
          alt={alt}
          draggable={false}
          style={style}
          sx={{
            position: "absolute",
            inset: 0,
            m: "auto",
            maxWidth: "92%",
            maxHeight: "86%",
            objectFit: "contain",
            userSelect: "none",
            borderRadius: scale > 1 ? 0 : 2,
            boxShadow: "0 30px 80px rgba(0,0,0,0.5)",
          }}
        />
      </Box>

      <Tooltip title="Close (Esc)">
        <IconButton aria-label="Close" onClick={onClose} sx={{ ...glassSx, position: "absolute", top: 16, right: 16 }}>
          <CloseRoundedIcon />
        </IconButton>
      </Tooltip>

      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{
          position: "absolute",
          bottom: 24,
          left: "50%",
          transform: "translateX(-50%)",
          p: 0.5,
          borderRadius: 999,
          bgcolor: "rgba(15, 23, 42, 0.6)",
          border: "1px solid rgba(255,255,255,0.12)",
          backdropFilter: "blur(12px)",
        }}
      >
        <IconButton aria-label="Zoom out" size="small" onClick={() => zoomBy(1 / 1.5)} disabled={scale <= minScale} sx={glassSx}>
          <ZoomOutRoundedIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ minWidth: 52, textAlign: "center", color: "#fff", fontSize: 13, fontWeight: 700 }}>
          {Math.round(scale * 100)}%
        </Typography>
        <IconButton aria-label="Zoom in" size="small" onClick={() => zoomBy(1.5)} disabled={scale >= maxScale} sx={glassSx}>
          <ZoomInRoundedIcon fontSize="small" />
        </IconButton>
        <Tooltip title="Reset">
          <span>
            <IconButton aria-label="Reset zoom" size="small" onClick={reset} disabled={scale === 1} sx={glassSx}>
              <CenterFocusStrongRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Typography
        sx={{
          position: "absolute",
          top: 26,
          left: "50%",
          transform: "translateX(-50%)",
          fontSize: 12.5,
          color: "rgba(255,255,255,0.6)",
          whiteSpace: "nowrap",
          pointerEvents: "none",
          display: { xs: "none", sm: "block" },
        }}
      >
        Scroll or pinch to zoom · Drag to move · Double-click to toggle
      </Typography>
    </Dialog>
  );
}

ImageLightbox.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default ImageLightbox;
